import * as React from 'react';
import {
  Box, Card, CardContent, createStyles,
  Grid, makeStyles, Paper, Typography, TextField, Button, CircularProgress, Avatar,
} from '@material-ui/core';
import useSWR from 'swr';
import CommentRoundedIcon from '@material-ui/icons/CommentRounded';
import type { AxiosError } from 'axios';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import clsx from 'clsx';

import globalUseStyles from '../../globalClasses';
import TableCellChip from '../TableCellChip';

const useStyles = makeStyles((theme) => createStyles({
  boxCount: {
    width: '20px',
    height: '20px',
    backgroundColor: '#F2F2F2',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginTop: '4px',
    float: 'right',
  },
  boxCountText: {
    fontWeight: 700,
  },
  trackingNumberText: {
    fontWeight: 500,
  },
  boxIcon: {
    fontSize: '18px',
  },
  boxTitle: {
    fontSize: '18px', fontWeight: 700,
  },
  cardContent: {
    paddingRight: '4px',
  },
  commentIcon: {
    marginRight: '0.5px',
  },
  addCommentButton: {
    color: theme.palette.common.white,
    backgroundColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
    },
  },
}));

interface EstimatorNote {
  comment: string,
  repairItemLabels: string[],
  jobName: string
}

interface EstimatorCommentProps {
  notes: Array<EstimatorNote>;
  repairId: string,
  allowNewComments: boolean
}

interface ExternalCommentsProps {
  externalComments: string;
}

const EstimatorCommentCards: React.FC<{ repairId: string, repairTableLength: number, allowNewComments: boolean, externalCommentsOnly?: boolean }> = ({
  repairId, repairTableLength, allowNewComments, externalCommentsOnly = false,
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [inputComment, setInputComment] = React.useState<string>('');
  const [fetching, setFetching] = React.useState(false);
  const globalStyles = globalUseStyles();

  const handleInputCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputComment(event.target.value);
  };

  const fetcher = (url: string, repairIdParameter: string) => axios.get<EstimatorCommentProps>(url, {
    params: {
      repairId: repairIdParameter,
    },
  }).then((response) => response.data);

  const { data, mutate } = useSWR<EstimatorCommentProps>(repairId
    ? ['/estimator/getrepairnotes', repairId, repairTableLength]
    : null,
  (url, repairIdParameter) => fetcher(url, repairIdParameter));

  const externalCommentsFetcher = (url: string, repairIdParameter: string) => axios.get<ExternalCommentsProps>(url, {
    params: {
      repairId: repairIdParameter,
    },
  }).then((response) => response.data);

  const { data: externalCommentsData } = useSWR<ExternalCommentsProps>(repairId
    ? ['/estimator/GetExternalComments', repairId]
    : null,
  (url, repairIdParameter) => externalCommentsFetcher(url, repairIdParameter));

  const submitNote = (repairIdInput: string, inputNote: string): void => {
    if (inputNote.length > 0) {
      setFetching(true);
      axios.post<string>('/estimator/addrepairnote', { repairId: repairIdInput, noteDetail: inputNote })
        .then(() => {
          mutate().catch((e) => { enqueueSnackbar(e, { persist: false, variant: 'error' }); });
          setInputComment('');
        })
        .catch((error: AxiosError): void => {
          enqueueSnackbar(error?.response?.data, { persist: false, variant: 'error' });
        })
        .finally(() => {
          setFetching(false);
        });
    }
  };

  const formattedContent = externalCommentsData ? externalCommentsData.externalComments.replace(/\r?\n/g, '<br>') : '';

  const classes = useStyles();

  return (
    <>
      <Box>
        <Grid container justifyContent="space-between" spacing={0}>
          <Grid item>
            <Avatar className={clsx(globalStyles.smallAvatar, globalStyles.blueIcon)}>
              <CommentRoundedIcon className={clsx(globalStyles.smallIconSize, classes.commentIcon)} />
            </Avatar>
          </Grid>
          <Grid item xs={10}>
            <Typography variant="subtitle2" className={classes.boxTitle}>
              {'Comments'}
            </Typography>
          </Grid>
          <Grid item xs={1}>
            <Paper className={classes.boxCount} elevation={0}>
              <Typography variant="body2" className={classes.boxCountText}>
                {
                  // eslint-disable-next-line no-nested-ternary
                  externalCommentsOnly ? (formattedContent ? 1 : 0) : (data?.notes?.length || '0')
                }
              </Typography>
            </Paper>
          </Grid>
        </Grid>
        <Box paddingBottom={1} />
        {
          externalCommentsOnly
            ? (
              <Box pb={12}>
                <Grid container>
                  <Grid item xs={12}>
                    <Box pt={2}>
                      <Card>
                        <CardContent className={classes.cardContent}>
                          <Grid container spacing={0}>
                            <Grid item xs={11}>
                              <Typography variant="body1" color="textPrimary" className={classes.trackingNumberText}>
                                {
                                  // eslint-disable-next-line react/no-danger
                                  <div dangerouslySetInnerHTML={{ __html: formattedContent || '' }} />
                                }
                              </Typography>
                            </Grid>
                          </Grid>
                          <Box pt={0.5}>
                            <TableCellChip
                              label={formattedContent ? 'External Comments' : 'No External Comments'}
                              backgroundColor="#DAEADC"
                              fontColor="#6CAE75"
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            )
            : (
              <Box pb={12}>
                <Grid container>
                  {data?.notes?.map((repairNote) => (
                    <Grid item xs={12} key={repairNote.comment + repairNote.jobName}>
                      <Box pt={2}>
                        <Card>
                          <CardContent className={classes.cardContent}>
                            <Grid container spacing={0}>
                              <Grid item xs={11}>
                                <Typography variant="body1" color="textPrimary" className={classes.trackingNumberText} dangerouslySetInnerHTML={{ __html: repairNote.comment }} />
                              </Grid>
                            </Grid>
                            <Box pt={0.5}>
                              {repairNote.repairItemLabels.join(', ') === 'All Items'
                                ? (
                                  <TableCellChip
                                    label={repairNote.repairItemLabels.join(', ') + (repairNote.jobName
                                      ? ` for ${repairNote.jobName}` : '')}
                                    backgroundColor="#DAEADC"
                                    fontColor="#6CAE75"
                                  />
                                )
                                : (
                                  <TableCellChip
                                    label={repairNote.repairItemLabels.join(', ') + (repairNote.jobName
                                      ? ` for ${repairNote.jobName}` : '')}
                                    backgroundColor="#FDECCC"
                                    fontColor="#FAB635"
                                  />
                                )}
                            </Box>
                          </CardContent>
                        </Card>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )
        }
        {
          allowNewComments && !externalCommentsOnly
          && (
            <form>
              <TextField
                label="Comments"
                id="comments-text-field"
                fullWidth
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                  focused: true,
                }}
                value={inputComment}
                onChange={handleInputCommentChange}
              />
              <Box pt={1}>
                <Button
                  onClick={
                    () => {
                      submitNote(repairId, inputComment);
                    }
                  }
                  id="add-comment-button"
                  variant="contained"
                  color="inherit"
                  className={classes.addCommentButton}
                  fullWidth
                  disabled={fetching}
                >
                  {fetching ? <CircularProgress size={25} /> : 'Add Comment'}
                </Button>
              </Box>
            </form>
          )
        }
      </Box>
    </>

  );
};

export default EstimatorCommentCards;
